import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import ToolPrimer from "@/components/ToolPrimer";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const CURRENCIES = {
  EUR: { symbol: "\u20AC", locale: "en-IE" },
  USD: { symbol: "$", locale: "en-US" },
  GBP: { symbol: "\u00A3", locale: "en-GB" },
};

export default function CompoundInterest() {
  const [initial, setInitial] = useState(10000);
  const [monthly, setMonthly] = useState(500);
  const [rate, setRate] = useState(7);
  const [years, setYears] = useState(20);
  const [currency, setCurrency] = useState("EUR");

  const { data, finalValue, contributed, interest } = useMemo(
    () => project(initial, monthly, rate, years),
    [initial, monthly, rate, years]
  );

  const cur = CURRENCIES[currency];
  const fmt = (n) =>
    cur.symbol +
    new Intl.NumberFormat(cur.locale, {
      maximumFractionDigits: 0,
    }).format(Math.round(n));

  const fmtCompact = (n) =>
    cur.symbol +
    new Intl.NumberFormat(cur.locale, {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(n);

  return (
    <main data-testid="compound-interest-tool" className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
      <Seo
        title="Compound Interest Calculator"
        description="Model long-horizon growth of an initial capital plus recurring contributions. Uses effective annual returns — the annual rate you type is what a lump sum earns in one year."
        path="/tools/compound-interest"
      />
      <Link
        to="/tools"
        data-testid="tool-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-foreground/60 hover:text-primary transition-colors mb-10"
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> All tools
      </Link>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60 mb-3">
            Financial tool · Compound Interest
          </div>
          <h1 className="font-serif text-5xl md:text-6xl font-light leading-[1.02] tracking-tighter">
            The real magic,
            <br />
            <span className="italic text-primary">measured.</span>
          </h1>
        </div>
        <p className="max-w-sm text-sm text-foreground/60 leading-relaxed">
          The annual rate is treated as an <em>effective annual return</em>:
          on a lump sum, the horizon-end value equals initial × (1 + r)<sup>years</sup>.
          Monthly contributions are added at the end of each month. Not investment
          advice — a thinking tool.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        {/* Inputs */}
        <section className="lg:col-span-4 border border-border rounded-sm p-8 bg-secondary/40" data-testid="ci-inputs">
          <div className="flex items-center justify-between mb-8">
            <div className="text-xs uppercase tracking-[0.25em] text-foreground/60">
              Assumptions
            </div>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              data-testid="ci-currency"
              className="text-xs uppercase tracking-widest bg-background border border-border rounded-full px-3 py-1.5 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
            >
              {Object.keys(CURRENCIES).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <Field
            label="Initial capital"
            value={initial}
            display={fmt(initial)}
            min={0}
            max={500000}
            step={500}
            onChange={setInitial}
            testId="ci-initial"
          />
          <Field
            label="Monthly contribution"
            value={monthly}
            display={fmt(monthly)}
            min={0}
            max={10000}
            step={50}
            onChange={setMonthly}
            testId="ci-monthly"
          />
          <Field
            label="Annual return"
            value={rate}
            display={`${rate.toFixed(1)}%`}
            min={0}
            max={20}
            step={0.1}
            onChange={setRate}
            testId="ci-rate"
          />
          <Field
            label="Horizon"
            value={years}
            display={`${years} ${years === 1 ? "year" : "years"}`}
            min={1}
            max={50}
            step={1}
            onChange={setYears}
            testId="ci-years"
            last
          />
        </section>

        {/* Outputs */}
        <section className="lg:col-span-8 space-y-8">
          {/* Final value hero card */}
          <div className="border border-border rounded-sm p-8 md:p-10 bg-background relative overflow-hidden">
            <div className="text-[10px] uppercase tracking-[0.3em] text-foreground/60">
              Projected value in {years} {years === 1 ? "year" : "years"}
            </div>
            <div
              className="font-serif text-5xl md:text-7xl font-light tracking-tighter mt-4"
              data-testid="ci-final-value"
            >
              {fmt(finalValue)}
            </div>
            <div className="mt-8 grid grid-cols-2 gap-8 pt-6 border-t border-border">
              <Stat
                label="Total contributed"
                value={fmt(contributed)}
                sub={pct(contributed, finalValue) + " of total"}
                testId="ci-contributed"
              />
              <Stat
                label="Interest earned"
                value={fmt(interest)}
                sub={pct(interest, finalValue) + " of total"}
                accent
                testId="ci-interest"
              />
            </div>
          </div>

          {/* Chart */}
          <div className="border border-border rounded-sm p-6 md:p-8 bg-background" data-testid="ci-chart">
            <div className="flex items-center justify-between mb-6">
              <div className="text-xs uppercase tracking-[0.25em] text-foreground/60">
                Growth curve
              </div>
              <Legend />
            </div>
            <div className="h-[360px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gContrib" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0.05} />
                    </linearGradient>
                    <linearGradient id="gInterest" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.55} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.08} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
                  <XAxis
                    dataKey="year"
                    tickLine={false}
                    axisLine={{ stroke: "hsl(var(--border))" }}
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                  />
                  <YAxis
                    tickFormatter={fmtCompact}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    width={70}
                  />
                  <Tooltip
                    content={<ChartTooltip fmt={fmt} />}
                    cursor={{ stroke: "hsl(var(--primary))", strokeOpacity: 0.15, strokeWidth: 30 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="contributed"
                    stackId="1"
                    stroke="hsl(var(--muted-foreground))"
                    strokeWidth={1.25}
                    fill="url(#gContrib)"
                  />
                  <Area
                    type="monotone"
                    dataKey="interest"
                    stackId="1"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    fill="url(#gInterest)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>
      </div>

      <blockquote className="mt-20 max-w-3xl mx-auto text-center font-serif text-2xl md:text-3xl italic text-foreground/75 leading-snug">
        “Little by little steps creates wonders. Do not interrupt it
        unnecessarily.”
        <span className="block mt-4 not-italic text-xs uppercase tracking-widest text-foreground/50">
          — Third law of reasonable investment decisions
        </span>
      </blockquote>

      <ToolPrimer
        title="Compound interest, as it actually behaves."
        sections={[
          {
            heading: "What it does",
            body: "The calculator projects the value of an initial capital plus a fixed monthly contribution, compounded at a rate you set, over a horizon you choose. The stacked chart separates what you actually put in from what the market gives back to you — because that split, more than the final number, is what tells you whether the plan works.",
          },
          {
            heading: "Why the rate is 'effective annual'",
            body: "The percentage you type is the effective annual return: a lump sum grows by exactly that much in one year. Internally, monthly compounding uses r_month = (1 + r_annual)^(1/12) − 1, so lump-sum growth matches (1 + r)^years exactly. This is different from the APR convention (r/12) that overstates results — a subtle 0.2 to 0.4 percentage-point error over long horizons.",
          },
          {
            heading: "How to read it",
            body: "Contributions form the grey base of the chart; interest is the green wedge that widens with time. Early on, interest is a rounding error. Somewhere between years ten and fifteen it usually overtakes contributions — that crossover is the whole point of the exercise. Slide the horizon back and forth and watch where it lands: if the plan needs it earlier than that, you probably need either more monthly savings or a more realistic return assumption.",
          },
        ]}
        caveat="This is a nominal projection. Real returns are lower than nominal after inflation and taxes, and constant-rate assumptions mask sequence-of-returns risk. Use this as a thinking tool, not a forecast."
      />
    </main>
  );
}

/* ---------------- Reusable field with slider + numeric input ---------------- */
function Field({ label, value, display, min, max, step, onChange, testId, last }) {
  return (
    <div className={last ? "" : "mb-8"}>
      <div className="flex items-baseline justify-between mb-3">
        <label className="text-xs uppercase tracking-[0.2em] text-foreground/70">
          {label}
        </label>
        <input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (!isNaN(v)) onChange(clamp(v, min, max));
          }}
          data-testid={`${testId}-input`}
          className="w-28 text-right bg-transparent font-serif text-lg tabular-nums focus:outline-none border-b border-border focus:border-primary transition-colors"
        />
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(v[0])}
        data-testid={`${testId}-slider`}
        className="mt-2"
      />
      <div className="mt-2 text-[10px] uppercase tracking-widest text-foreground/40">
        {display}
      </div>
    </div>
  );
}

function Stat({ label, value, sub, accent, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-foreground/50">
        {label}
      </div>
      <div className={`font-serif text-3xl md:text-4xl mt-2 ${accent ? "text-primary" : ""}`}>
        {value}
      </div>
      <div className="text-xs text-foreground/50 mt-1">{sub}</div>
    </div>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-5 text-[10px] uppercase tracking-widest text-foreground/60">
      <span className="inline-flex items-center gap-2">
        <span className="w-3 h-3 rounded-sm bg-muted-foreground/40 border border-muted-foreground/60" />
        Contributed
      </span>
      <span className="inline-flex items-center gap-2">
        <span className="w-3 h-3 rounded-sm bg-primary/60 border border-primary" />
        Interest
      </span>
    </div>
  );
}

function ChartTooltip({ active, payload, label, fmt }) {
  if (!active || !payload || !payload.length) return null;
  const contributed = payload.find((p) => p.dataKey === "contributed")?.value || 0;
  const interest = payload.find((p) => p.dataKey === "interest")?.value || 0;
  const total = contributed + interest;
  return (
    <div className="border border-border bg-background/95 backdrop-blur-md rounded-sm px-4 py-3 shadow-sm">
      <div className="text-[10px] uppercase tracking-widest text-foreground/50 mb-1">
        Year {label}
      </div>
      <div className="font-serif text-xl">{fmt(total)}</div>
      <div className="mt-2 text-xs text-foreground/60 space-y-0.5">
        <div>Contributed: {fmt(contributed)}</div>
        <div className="text-primary">Interest: {fmt(interest)}</div>
      </div>
    </div>
  );
}

/* ---------------- Math ---------------- */
function project(initial, monthly, annualRatePct, years) {
  // Effective annual return: convert to equivalent monthly rate so that
  //   (1 + r_month)^12 == (1 + annualRatePct/100)
  // This preserves lump-sum result: initial * (1+r)^years exactly.
  const r = Math.pow(1 + annualRatePct / 100, 1 / 12) - 1;
  const data = [{ year: 0, contributed: initial, interest: 0, total: initial }];
  let balance = initial;
  let contributed = initial;
  for (let month = 1; month <= years * 12; month++) {
    balance = balance * (1 + r) + monthly;
    contributed += monthly;
    if (month % 12 === 0) {
      const year = month / 12;
      const interest = Math.max(0, balance - contributed);
      data.push({
        year,
        contributed: Math.round(contributed),
        interest: Math.round(interest),
        total: Math.round(balance),
      });
    }
  }
  return {
    data,
    finalValue: balance,
    contributed,
    interest: Math.max(0, balance - contributed),
  };
}

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}

function pct(part, total) {
  if (!total) return "0%";
  return `${Math.round((part / total) * 100)}%`;
}
